import React, { useCallback, useEffect } from 'react';
import Stack from '@mui/material/Stack';
import DashboardBlock from './DashboardBlock';
import { useAppDispatch, useAppSelector } from 'store';
import { selectUser } from 'store/general/general-slice-selectors';
import OrganizationRole from 'models/enums/organization-roles';
import { useNavigate, useSearchParams } from 'react-router-dom';
import SphereNavigationRoutes from '@common/enums/query-params/sphere-navigation-routes';
import DatasetBlock from './dataset/DatasetBlock';
import { FilterKeys } from '@common/enums/query-params/filters';
import { setCurrentDashboardById } from 'store/fleet-management/fleet-management-slice';
import { selectFleetDashboardsData } from 'store/fleet-management/fleet-management-slice-selectors';
import { DashboardViewType } from 'models/fleet-management/FleetDashboard';

const OrbitDashboard: React.FC<{ datasetPage?: boolean }> = ({ datasetPage }) => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const [queryParams, setQueryParams] = useSearchParams();
  const queryDashboardId = queryParams.get(FilterKeys.FleetDashboard);
  const queryDashboardView = queryParams.get(FilterKeys.DashboardView);

  const currentUser = useAppSelector((state) => selectUser(state));
  const fleetDashboardsData = useAppSelector((state) => selectFleetDashboardsData(state));

  const isDatasetsFiltersPage =
    window.location.pathname === SphereNavigationRoutes.InsightsDatasets;

  const backToSphereHandler = useCallback(() => {
    navigate(SphereNavigationRoutes.Dashboard);
  }, [navigate]);

  useEffect(() => {
    if (currentUser.data.organization.role !== OrganizationRole.MANUFACTURER) {
      backToSphereHandler();
    }
  }, [backToSphereHandler, currentUser.data.organization.role]);

  // An orbit view with nothing selected needs a dashboard to show, so the first one stands in.
  //
  // Only when nothing is selected. Once the url names a dashboard or a generic view the selection
  // has been made elsewhere, and this view is on its way out: a render still happens here before
  // the parent has caught up, and standing in during it would drag the url back to an orbit board
  // and undo the navigation.
  useEffect(() => {
    if (isDatasetsFiltersPage || queryDashboardId != null || queryDashboardView != null) return;

    const orbitDashboards =
      fleetDashboardsData.data?.filter((db) => db.viewType === DashboardViewType.ORBIT) ?? [];
    if (orbitDashboards.length === 0) return;

    setQueryParams((prevState) => {
      prevState.set(FilterKeys.FleetDashboard, orbitDashboards[0].id);
      return prevState;
    });
  }, [
    fleetDashboardsData.data,
    isDatasetsFiltersPage,
    queryDashboardId,
    queryDashboardView,
    setQueryParams,
  ]);

  useEffect(() => {
    if (queryDashboardId != null) {
      dispatch(setCurrentDashboardById(queryDashboardId));
    }
  }, [dispatch, queryDashboardId]);

  return (
    <React.Fragment>
      <Stack
        direction="row"
        width="100%"
        height="100%"
        overflow="hidden"
        p={2}
        spacing={2}
        position="relative"
      >
        {datasetPage ? <DatasetBlock /> : <DashboardBlock />}
      </Stack>
    </React.Fragment>
  );
};

export default OrbitDashboard;
